import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authProvider, AuthError, type AuthSession } from '@/lib/auth';

interface AuthState {
  session: AuthSession | null;
  status: 'idle' | 'authenticating' | 'authenticated' | 'error';
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  hydrate: () => Promise<void>;
}

/**
 * Auth store. Delega al `authProvider` activo (mock o Cognito real).
 *
 * Persistencia: la sesión se guarda en `localStorage` bajo `condo.session`
 * para sobrevivir reload. En modo Cognito real, el SDK también guarda sus
 * propios tokens bajo `CognitoIdentityServiceProvider.*` — los dos
 * sobreviven y `restoreSession()` los reconcilia al arrancar.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      status: 'idle',
      error: null,

      signIn: async (email, password) => {
        set({ status: 'authenticating', error: null });
        try {
          const session = await authProvider.signIn(email, password);
          set({ session, status: 'authenticated', error: null });
        } catch (err) {
          const msg = mapError(err);
          set({ status: 'error', error: msg, session: null });
          throw err;
        }
      },

      signOut: async () => {
        await authProvider.signOut();
        set({ session: null, status: 'idle', error: null });
      },

      hydrate: async () => {
        const session = await authProvider.restoreSession();
        if (session) {
          set({ session, status: 'authenticated' });
        } else if (get().session) {
          set({ session: null, status: 'idle' });
        }
      },
    }),
    {
      name: 'condo.session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ session: state.session }),
    },
  ),
);

function mapError(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case 'invalid_credentials':
        return 'Las credenciales no son válidas.';
      case 'user_not_found':
        return 'No encontramos una cuenta con ese correo.';
      case 'new_password_required':
        return 'Debes establecer una nueva contraseña. Revisa tu email.';
      case 'network_error':
        return 'Error de red. Verifica tu conexión.';
      default:
        return 'No se pudo iniciar sesión. Intenta de nuevo.';
    }
  }
  return 'No se pudo iniciar sesión. Intenta de nuevo.';
}
